import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useWalkthrough, useWalkthroughConfig } from "./WalkthroughContext";

const SWIPE_THRESHOLD = 50;

export function IntroCards() {
  const { introStep, nextIntro, prevIntro, skipIntro, finishIntro } = useWalkthrough();
  const { introCards } = useWalkthroughConfig();
  const [direction, setDirection] = useState(0);
  const card = introCards[introStep];
  const isLast = introStep === introCards.length - 1;

  function handleDragEnd(_: unknown, info: { offset: { x: number } }) {
    if (info.offset.x < -SWIPE_THRESHOLD && !isLast) {
      setDirection(1);
      nextIntro();
    } else if (info.offset.x > SWIPE_THRESHOLD && introStep > 0) {
      setDirection(-1);
      prevIntro();
    }
  }

  function handleNext() {
    if (isLast) {
      finishIntro();
    } else {
      setDirection(1);
      nextIntro();
    }
  }

  if (!card) return null;
  const Icon = card.icon;

  return (
    <div className="fixed inset-0 z-[70] flex flex-col bg-bg safe-top safe-bottom">
      {/* Skip button */}
      <div className="flex justify-end px-6 pt-4">
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={skipIntro}
          className="text-sm font-medium text-muted hover:text-foreground transition-colors"
        >
          Skip
        </motion.button>
      </div>

      {/* Card content */}
      <div className="flex flex-1 items-center justify-center px-8 overflow-hidden">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={introStep}
            custom={direction}
            initial={{ opacity: 0, x: direction >= 0 ? 80 : -80 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction >= 0 ? -80 : 80 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={handleDragEnd}
            className="flex w-full flex-col items-center text-center"
          >
            <div className="flex h-[120px] w-[120px] items-center justify-center rounded-full bg-accent/10">
              <Icon className="h-12 w-12 text-accent" />
            </div>
            <h2 className="mt-8 text-2xl font-semibold font-display text-foreground">
              {card.title}
            </h2>
            <p className="mt-3 text-base text-muted leading-relaxed max-w-xs">
              {card.description}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bottom controls */}
      <div className="px-8 pb-8 space-y-6">
        {/* Dot indicator */}
        <div className="flex items-center justify-center gap-2">
          {introCards.map((_, i) => (
            <motion.div
              layout
              key={i}
              className="h-2 rounded-full"
              initial={false}
              animate={{
                width: i === introStep ? 24 : 8,
                backgroundColor: i === introStep ? "#2D5A4A" : "hsl(var(--border))",
              }}
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
            />
          ))}
        </div>

        {/* Next / Get Started button */}
        <motion.button
          whileTap={{ scale: 0.96 }}
          onClick={handleNext}
          className="w-full rounded-xl py-3.5 text-base font-semibold text-white transition-colors"
          style={{ backgroundColor: "#2D5A4A" }}
        >
          {isLast ? "Get Started" : "Next"}
        </motion.button>
      </div>
    </div>
  );
}
